import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, AlertCircle } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { usePageTitle } from "@/hooks/usePageTitle";
import { Reveal, Chapter } from "@/components/Shared";

const SECTOR_HERO_IMAGES = {
  restauration:
    "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&w=1600&q=80",
  "retail-alimentaire":
    "https://images.unsplash.com/photo-1542838132-92c53300491e?auto=format&fit=crop&w=1600&q=80",
  "retail-specialise":
    "https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&w=1600&q=80",
  "reseaux-franchises":
    "https://images.unsplash.com/photo-1481437156560-3205f6a55735?auto=format&fit=crop&w=1600&q=80",
};

const SectorPage = () => {
  const { slug } = useParams();
  const { t } = useLanguage();
  const s = t.sectorPages && t.sectorPages[slug];
  usePageTitle(s ? s.name : undefined);

  if (!s) {
    return (
      <main data-testid="sector-not-found" className="pt-[76px]">
        <div className="mx-auto max-w-3xl px-5 py-32 text-center">
          <p className="text-slate-500">404</p>
          <Link to="/pour-qui" className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-blue-600">
            <ArrowLeft className="h-4 w-4" /> {t.nav.who}
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main data-testid={`sector-page-${slug}`} className="pt-[76px]">
      <section className="relative overflow-hidden bg-[#0A0F1D]">
        <img
          src={SECTOR_HERO_IMAGES[slug]}
          alt={s.name}
          className="absolute inset-0 h-full w-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0F1D] via-[#0A0F1D]/60 to-[#0A0F1D]/30" />
        <div className="relative mx-auto max-w-7xl px-5 py-24 lg:px-8 lg:py-36">
          <Reveal>
            <Link
              to="/pour-qui"
              data-testid="sector-back-link"
              className="inline-flex items-center gap-2 text-sm font-bold text-slate-300 transition-colors hover:text-white"
            >
              <ArrowLeft className="h-4 w-4" /> {t.nav.who}
            </Link>
            <h1 className="mt-6 max-w-3xl font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl text-balance">
              {s.name}
            </h1>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg">{s.hero}</p>
            <div className="mt-7 flex flex-wrap gap-2" data-testid="sector-tags">
              {s.tags.map((tag) => (
                <span
                  key={tag}
                  className="rounded-full border border-white/15 bg-white/10 px-3.5 py-1.5 text-xs font-bold text-white backdrop-blur"
                >
                  {tag}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <p className="max-w-3xl text-base leading-relaxed text-slate-600 sm:text-lg">{s.intro}</p>
          </Reveal>
          <div className="mt-16 grid gap-12 lg:grid-cols-2">
            <Reveal>
              <Chapter index="01" label={s.challengesTitle} />
              <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900 lg:text-3xl text-balance">
                {s.challengesTitle}
              </h2>
              <ul className="mt-8 space-y-4">
                {s.challenges.map((challenge, i) => (
                  <li
                    key={challenge}
                    data-testid={`sector-challenge-${i}`}
                    className="flex items-start gap-3.5 rounded-xl border border-slate-100 bg-white p-5"
                  >
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-rose-50">
                      <AlertCircle className="h-4 w-4 text-rose-500" />
                    </span>
                    <p className="pt-1.5 text-sm font-medium leading-relaxed text-slate-700">{challenge}</p>
                  </li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={0.12}>
              <Chapter index="02" label={s.solutionsTitle} />
              <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900 lg:text-3xl text-balance">
                {s.solutionsTitle}
              </h2>
              <ul className="mt-8 space-y-4">
                {s.solutions.map((solution, i) => (
                  <li
                    key={solution}
                    data-testid={`sector-solution-${i}`}
                    className="flex items-start gap-3.5 rounded-xl border border-emerald-100 bg-emerald-50/50 p-5"
                  >
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-emerald-100">
                      <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                    </span>
                    <p className="pt-1.5 text-sm font-medium leading-relaxed text-slate-700">{solution}</p>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="pb-20 lg:pb-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <div className="relative flex flex-col items-start justify-between gap-8 overflow-hidden rounded-2xl bg-[#0A0F1D] p-9 lg:flex-row lg:items-center lg:p-14">
              <div className="pointer-events-none absolute inset-0 bg-grid-navy" />
              <div className="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full bg-emerald-500/15 blur-3xl" />
              <div className="relative">
                <h2 className="font-display text-2xl font-bold text-white lg:text-3xl text-balance">{s.ctaTitle}</h2>
                <p className="mt-3 max-w-md text-sm text-slate-400 sm:text-base">{s.ctaText}</p>
              </div>
              <Link
                to="/demo"
                data-testid="sector-cta-button"
                className="group relative inline-flex shrink-0 items-center gap-2 rounded-xl bg-blue-600 px-7 py-4 text-sm font-bold text-white transition-colors duration-300 hover:bg-blue-500"
              >
                {s.ctaButton}
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default SectorPage;
